/**
 * Keycloak Helper Functions
 * Pure utility functions for Keycloak operations
 */

/**
 * Determines the redirect URI based on environment
 * @param {boolean} isDevelopment - Whether app is in development mode
 * @param {string} additionalParams - Additional query parameters
 * @returns {string} The redirect URI
 */
export const getRedirectUri = (isDevelopment, additionalParams = '') => {
    const baseUri = isDevelopment 
        ? 'http://localhost:8415/' 
        : window.location.origin + '/';
    return additionalParams ? baseUri + additionalParams : baseUri;
};

/**
 * Checks if current path is a public route
 * @param {string} path - Current path
 * @param {string[]} publicRoutes - Array of public route patterns
 * @returns {boolean} True if path is public
 */
export const isPublicRoute = (path, publicRoutes) => {
    return publicRoutes.some(route => path.startsWith(route));
};

/**
 * Clears all browser storage (cookies, localStorage, sessionStorage)
 * @param {string[]} localStorageKeysToKeep - Keys to preserve in localStorage
 */
export const clearBrowserStorage = (localStorageKeysToKeep = []) => {
    // Clear all cookies
    document.cookie.split(";").forEach((c) => {
        document.cookie = c
            .replace(/^ +/, "")
            .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    
    // Clear localStorage (keep specified keys)
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
        if (!localStorageKeysToKeep.includes(key)) {
            localStorage.removeItem(key);
        }
    });
    
    // Clear sessionStorage
    sessionStorage.clear();
};

/**
 * Extracts user initials from full name
 * @param {string} fullName - User's full name
 * @returns {string} User initials (e.g., "JD" for "John Doe")
 */
export const getUserInitials = (fullName = '') => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Checks if running in test environment
 * @returns {boolean} True if test environment
 */
export const isTestEnvironment = () => {
    return process.env.NODE_ENV === 'test' || process.env.REACT_APP_SKIP_KEYCLOAK_INIT === 'true';
};
